// src/pages/dashboard/DashboardKaprodi.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FileText,
    Plus,
    AlertTriangle,
    Clock,
    CheckCircle,
    Eye,
    Edit,
    Send,
    TrendingUp,
    Building2,
    ChevronRight,
    Hourglass,
    Target,
    Calendar,
    BarChart3,
    AlertCircle,
    ExternalLink,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, ProgressBar, Loading,
} from '../../components/ui';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { StatusLaporan, TingkatRisiko } from '../../types';

// ========== CONSTANTS ==========
const MAX_ITEMS_DASHBOARD = 3; // Maksimal item per section di dashboard
const MAX_DEADLINES = 5; // Maksimal deadline yang ditampilkan

// ========== TYPES ==========
interface KpiData {
    total_laporan: number;
    draft: number;
    diajukan: number;
    dalam_proses: number;
    menunggu_konfirmasi: number;
    selesai: number;
    ditolak: number;
}

interface TindakLanjutItem {
    id: number;
    laporan_masalah_id?: number;
    laporan_masalah?: {
        id: number;
        kode: string;
        judul: string;
        status: string;
    };
    laporanMasalah?: {
        id: number;
        kode: string;
        judul: string;
        status: string;
    };
    program?: string;
    target_date?: string;
    target?: string;
    status: string;
    progress_percentage: number;
    pic_user_id?: number;
    pic_user?: {
        id: number;
        name: string;
    };
    alih_tanggung_jawab?: {
        pic_lama_id: number;
        pic_baru_id: number;
        pic_baru: string;
        tanggal: string;
    };
    is_pic_lama?: boolean;
    kondisi_selesai?: string;
}

interface LaporanItem {
    id: number;
    kode: string;
    judul: string;
    deskripsi?: string;
    status: string;
    tingkat_risiko: string;
    target_selesai?: string;
    completeness_percentage?: number;
    user_id?: number;
    user?: { id: number; name: string };
    unit?: { id: number; name: string };
    active_ppepp_rekomendasi?: {
        pic_user_id?: number;
    };
    activePpeppRekomendasi?: {
        pic_user_id?: number;
    };
}

// ========== HELPER COMPONENTS ==========

interface TindakLanjutCardProps {
    item: TindakLanjutItem;
    type: 'aktif' | 'menunggu_konfirmasi' | 'selesai' | 'dialihkan';
    isBaruDialihkan?: boolean;
    compact?: boolean;
}

const TindakLanjutCard: React.FC<TindakLanjutCardProps> = ({
    item,
    type,
    isBaruDialihkan,
    compact = false,
}) => {
    const statusConfig = {
        aktif: { class: 'bg-warning/10 text-warning', label: 'Proses' },
        menunggu_konfirmasi: {
            class: 'bg-purple-100 text-purple-700',
            label: 'Menunggu Konfirmasi',
        },
        selesai: { class: 'bg-success/10 text-success', label: '✓ Selesai' },
        dialihkan: { class: 'bg-gray-100 text-gray-500', label: 'Dialihkan' },
        terhambat: { class: 'bg-danger/10 text-danger', label: 'Terhambat' },
    };

    const isDialihkan = type === 'dialihkan';
    const isTerhambat = item.status === 'terhambat';
    const config = isTerhambat ? statusConfig.terhambat : statusConfig[type];
    const canUpdate = !isDialihkan && type === 'aktif' && !isTerhambat;

    const judul =
        item.laporan_masalah?.judul || item.laporanMasalah?.judul || item.program;
    const targetDate = item.target_date || item.target;
    const laporanId =
        item.laporan_masalah_id ||
        item.laporan_masalah?.id ||
        item.laporanMasalah?.id;

    // Hitung sisa hari
    const daysRemaining = targetDate
        ? Math.ceil(
            (new Date(targetDate).getTime() - new Date().setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24)
        )
        : null;

    const isOverdue = daysRemaining !== null && daysRemaining < 0;
    const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;

    return (
        <div
            className={`p-3 bg-neutral-bg rounded-xl ${isDialihkan ? 'opacity-60' : ''} ${isOverdue && type === 'aktif' ? 'border-l-4 border-l-danger' : ''
                } ${isUrgent && type === 'aktif' ? 'border-l-4 border-l-warning' : ''}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className={`font-bold truncate ${compact ? 'text-xs' : 'text-sm'}`} title={judul}>
                        {judul}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-neutral-text-secondary`}>
                            {targetDate
                                ? new Date(targetDate).toLocaleDateString('id-ID', {
                                    day: 'numeric',
                                    month: 'short',
                                })
                                : '-'}
                        </span>
                        {type === 'aktif' && daysRemaining !== null && (
                            <span
                                className={`${compact ? 'text-[10px]' : 'text-xs'} font-semibold ${isOverdue
                                    ? 'text-danger'
                                    : isUrgent
                                        ? 'text-warning'
                                        : 'text-neutral-text-secondary'
                                    }`}
                            >
                                {isOverdue
                                    ? `${Math.abs(daysRemaining)}hr terlambat`
                                    : `${daysRemaining}hr lagi`}
                            </span>
                        )}
                        <span
                            className={`px-1.5 py-0.5 rounded-full ${compact ? 'text-[9px]' : 'text-[10px]'} font-semibold ${config.class}`}
                        >
                            {compact ? (item.progress_percentage || 0) + '%' : config.label}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {!compact && (
                        <ProgressBar
                            value={item.progress_percentage || 0}
                            size="sm"
                            color={
                                item.progress_percentage >= 100
                                    ? 'success'
                                    : item.progress_percentage >= 50
                                        ? 'teal'
                                        : 'warning'
                            }

                        />
                    )}
                    {canUpdate ? (
                        <Link to={`/tindak-lanjut/${item.id}`}>
                            <button className="p-1.5 rounded-lg bg-teal-main/10 hover:bg-teal-main/20 transition-colors">
                                <TrendingUp className="w-3.5 h-3.5 text-teal-main" />
                            </button>
                        </Link>
                    ) : (
                        <Link to={`/laporan/${laporanId}`}>
                            <button className="p-1.5 rounded-lg bg-neutral-bg hover:bg-neutral-border/50 transition-colors">
                                <Eye className="w-3.5 h-3.5 text-neutral-text-secondary" />
                            </button>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

// Section Header dengan Link
interface SectionHeaderProps {
    icon: React.ReactNode;
    iconColor: string;
    title: string;
    count: number;
    total: number;
    linkTo: string;
    linkLabel?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
    icon,
    iconColor,
    title,
    count,
    total,
    linkTo,
    linkLabel = 'Lihat Semua',
}) => (
    <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${iconColor}`}></span>
            {title} ({count})
        </p>
        {total > count && (
            <Link
                to={linkTo}
                className="text-[10px] font-semibold text-teal-main hover:underline flex items-center gap-1"
            >
                {linkLabel} ({total}) <ExternalLink className="w-3 h-3" />
            </Link>
        )}
    </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
    <p className="text-sm text-neutral-text-secondary text-center py-4">
        {message}
    </p>
);

// ========== KPI CARD COMPONENT ==========
interface KpiCardProps {
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    value: number | string;
    valueColor?: string;
    subtitle?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
    icon,
    iconBg,
    label,
    value,
    valueColor = 'text-neutral-text-primary',
    subtitle,
}) => (
    <Card padding="sm" className="hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${iconBg}`}>{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    {label}
                </p>
                <p className={`text-2xl font-black ${valueColor}`}>{value}</p>
                {subtitle && (
                    <p className="text-[10px] text-neutral-text-secondary truncate">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    </Card>
);

// ========== DEADLINE CARD ==========
interface DeadlineCardProps {
    item: TindakLanjutItem;
    daysRemaining: number;
}

const DeadlineCard: React.FC<DeadlineCardProps> = ({ item, daysRemaining }) => {
    const isOverdue = daysRemaining < 0;
    const judul =
        item.laporan_masalah?.judul || item.laporanMasalah?.judul || item.program;

    return (
        <Link to={`/tindak-lanjut/${item.id}`}>
            <div
                className={`flex items-center gap-3 p-2.5 rounded-xl border transition-colors hover:bg-neutral-bg/50 ${isOverdue
                    ? 'bg-danger/5 border-danger/20'
                    : daysRemaining <= 3
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
            >
                <div
                    className={`text-center shrink-0 w-10 ${isOverdue
                        ? 'text-danger'
                        : daysRemaining <= 3
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                        }`}
                >
                    <p className="text-base font-black leading-none">
                        {isOverdue ? Math.abs(daysRemaining) : daysRemaining}
                    </p>
                    <p className="text-[8px] font-semibold uppercase">
                        {isOverdue ? 'Terlambat' : 'Hari'}
                    </p>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-neutral-text-primary truncate">
                        {judul}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 h-1 bg-neutral-border rounded-full overflow-hidden">
                            <div
                                className="h-full bg-teal-main rounded-full"
                                style={{ width: `${item.progress_percentage || 0}%` }}
                            />
                        </div>
                        <span className="text-[10px] text-neutral-text-secondary">
                            {item.progress_percentage || 0}%
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
};

// ========== MAIN COMPONENT ==========

const DashboardKaprodi: React.FC = () => {
    const { user } = useAuthStore();
    const prodi = user?.unit?.name || user?.role?.display_name || 'Unit Saya';
    const currentUserId = user?.id;

    // Loading & Data states
    const [loading, setLoading] = useState(true);
    const [kpiData, setKpiData] = useState<KpiData>({
        total_laporan: 0,
        draft: 0,
        diajukan: 0,
        dalam_proses: 0,
        menunggu_konfirmasi: 0,
        selesai: 0,
        ditolak: 0,
    });
    const [myLaporan, setMyLaporan] = useState<LaporanItem[]>([]);
    const [myTindakLanjut, setMyTindakLanjut] = useState<TindakLanjutItem[]>([]);

    // ========== FETCH DATA ==========
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [dashRes, laporanRes, tindakLanjutRes] = await Promise.all([
                    api.get('/dashboard'),
                    api.get('/laporan', { params: { per_page: 10, scope: 'pelapor' } }),
                    api.get('/monitoring/ppepp-progress', { params: { per_page: 100 } }),
                ]);

                // Parse dashboard data
                const d = dashRes.data?.data || dashRes.data;
                if (d) {
                    setKpiData({
                        total_laporan: d.total_laporan || 0,
                        draft: d.laporan_per_status?.draf || 0,
                        diajukan: d.laporan_per_status?.diajukan || 0,
                        dalam_proses:
                            d.laporan_per_status?.tugas_ppepp_aktif !== undefined
                                ? d.laporan_per_status.tugas_ppepp_aktif
                                : (d.laporan_per_status?.validasi_l1 || 0) +
                                (d.laporan_per_status?.validasi_l2 || 0) +
                                (d.laporan_per_status?.antrean_rapim || 0) +
                                (d.laporan_per_status?.diputuskan || 0) +
                                (d.laporan_per_status?.berlangsung || 0),
                        menunggu_konfirmasi:
                            d.laporan_per_status?.menunggu_konfirmasi || 0,
                        selesai:
                            (d.laporan_per_status?.selesai || 0) +
                            (d.laporan_per_status?.diarsipkan || 0),
                        ditolak:
                            (d.laporan_per_status?.ditolak_l1 || 0) +
                            (d.laporan_per_status?.ditolak_l2 || 0),
                    });
                }

                // Parse laporan data
                const lapData = laporanRes.data?.data || laporanRes.data;
                if (Array.isArray(lapData)) {
                    setMyLaporan(lapData);
                } else if (lapData?.data && Array.isArray(lapData.data)) {
                    setMyLaporan(lapData.data);
                }

                // Parse tindak lanjut data
                const tlData = tindakLanjutRes.data?.data || tindakLanjutRes.data;
                if (Array.isArray(tlData)) {
                    setMyTindakLanjut(tlData);
                } else if (tlData?.data && Array.isArray(tlData.data)) {
                    setMyTindakLanjut(tlData.data);
                }
            } catch (err) {
                console.error('Failed to load dashboard:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // ========== HELPER FUNCTIONS ==========

    const isDialihkan = (item: TindakLanjutItem): boolean => {
        if (!currentUserId) return false;
        const isPicLama = item.alih_tanggung_jawab?.pic_lama_id === currentUserId;
        const isBukanPicAktif = item.pic_user_id !== currentUserId;
        return isPicLama || (item.is_pic_lama === true && isBukanPicAktif);
    };

    const isBaruDialihkan = (item: TindakLanjutItem): boolean => {
        if (!currentUserId) return false;
        if (item.alih_tanggung_jawab?.pic_baru_id !== currentUserId) return false;
        if (!item.alih_tanggung_jawab?.tanggal) return false;
        const alihDate = new Date(item.alih_tanggung_jawab.tanggal).getTime();
        const diffDays = (Date.now() - alihDate) / (1000 * 60 * 60 * 24);
        return diffDays <= 3;
    };

    const isMenungguKonfirmasi = (item: TindakLanjutItem): boolean => {
        if (item.kondisi_selesai === 'selesai_final') return false;
        if (item.kondisi_selesai === 'menunggu_konfirmasi') return true;
        const laporanStatus = (
            item.laporan_masalah?.status ||
            item.laporanMasalah?.status ||
            ''
        ).toLowerCase();
        const ppeppStatus = (item.status || '').toLowerCase();
        const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);
        return (
            ppeppStatus === 'validasi' ||
            (ppeppStatus === 'selesai' &&
                item.progress_percentage >= 100 &&
                !isLaporanFinal)
        );
    };

    const isSelesaiFinal = (item: TindakLanjutItem): boolean => {
        if (item.kondisi_selesai === 'selesai_final') return true;
        const laporanStatus = (
            item.laporan_masalah?.status ||
            item.laporanMasalah?.status ||
            ''
        ).toLowerCase();
        const ppeppStatus = (item.status || '').toLowerCase();
        return ppeppStatus === 'selesai' && ['selesai', 'diarsipkan'].includes(laporanStatus);
    };

    const getDaysRemaining = (item: TindakLanjutItem): number => {
        const targetDate = item.target_date || item.target;
        if (!targetDate) return 999;
        return Math.ceil(
            (new Date(targetDate).getTime() - new Date().setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24)
        );
    };

    const getStatusAction = (status: string) => {
        switch (status) {
            case 'draf':
                return { label: 'Edit', icon: <Edit className="w-3 h-3" /> };
            case 'ditolak_l1':
            case 'ditolak_l2':
                return { label: 'Perbaiki', icon: <Edit className="w-3 h-3" /> };
            default:
                return { label: 'Detail', icon: <Eye className="w-3 h-3" /> };
        }
    };

    // ========== FILTER & SORT DATA ==========

    const relevantTindakLanjut = myTindakLanjut.filter((item) => {
        if (!currentUserId) return false;
        return item.pic_user_id === currentUserId || isDialihkan(item);
    });

    const tindakLanjutDialihkan = relevantTindakLanjut.filter((item) =>
        isDialihkan(item)
    );

    const tindakLanjutMenungguKonfirmasi = relevantTindakLanjut.filter(
        (item) => !isDialihkan(item) && isMenungguKonfirmasi(item)
    );

    const tindakLanjutSelesai = relevantTindakLanjut.filter(
        (item) => !isDialihkan(item) && isSelesaiFinal(item)
    );

    const tindakLanjutAktif = relevantTindakLanjut
        .filter(
            (item) =>
                !isDialihkan(item) &&
                !isMenungguKonfirmasi(item) &&
                !isSelesaiFinal(item) &&
                item.status !== 'dibatalkan' &&
                item.status !== 'batal'
        )
        // Sort: Overdue first, then by days remaining, then by progress (lowest first)
        .sort((a, b) => {
            const daysA = getDaysRemaining(a);
            const daysB = getDaysRemaining(b);
            // Overdue items first
            if (daysA < 0 && daysB >= 0) return -1;
            if (daysB < 0 && daysA >= 0) return 1;
            // Then by days remaining
            if (daysA !== daysB) return daysA - daysB;
            // Then by progress (lowest first)
            return (a.progress_percentage || 0) - (b.progress_percentage || 0);
        });

    // Stats
    const overdueCount = tindakLanjutAktif.filter(
        (item) => getDaysRemaining(item) < 0
    ).length;

    const urgentDeadlines = tindakLanjutAktif
        .filter((item) => getDaysRemaining(item) <= 7)
        .slice(0, MAX_DEADLINES);

    const avgProgress =
        tindakLanjutAktif.length > 0
            ? Math.round(
                tindakLanjutAktif.reduce(
                    (sum, item) => sum + (item.progress_percentage || 0),
                    0
                ) / tindakLanjutAktif.length
            )
            : 0;

    // Filter Laporan
    const laporanSayaSebagaiPicAktif = new Set(
        relevantTindakLanjut
            .filter((item) => item.pic_user_id === currentUserId && !isDialihkan(item))
            .map(
                (item) =>
                    item.laporan_masalah_id ||
                    item.laporan_masalah?.id ||
                    item.laporanMasalah?.id
            )
            .filter(Boolean)
    );

    const filteredLaporan = myLaporan.filter((lap) => {
        const status = (lap?.status || '').toLowerCase();
        if (
            ['berlangsung', 'selesai', 'diarsipkan'].includes(status) &&
            laporanSayaSebagaiPicAktif.has(lap.id)
        ) {
            return false;
        }
        return true;
    });

    // ========== STATUS FLOW CONFIG ==========
    const statusFlow = [
        { label: 'Draft', count: kpiData.draft, bg: '#9CA3AF', icon: <FileText className="w-3.5 h-3.5" /> },
        { label: 'Diajukan', count: kpiData.diajukan, bg: '#3B82F6', icon: <Send className="w-3.5 h-3.5" /> },
        { label: 'Proses', count: kpiData.dalam_proses, bg: '#F59E0B', icon: <Clock className="w-3.5 h-3.5" /> },
        { label: 'Konfirmasi', count: kpiData.menunggu_konfirmasi, bg: '#8B5CF6', icon: <Hourglass className="w-3.5 h-3.5" /> },
        { label: 'Selesai', count: kpiData.selesai, bg: '#22C55E', icon: <CheckCircle className="w-3.5 h-3.5" /> },
        { label: 'Ditolak', count: kpiData.ditolak, bg: '#EF4444', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    ];

    // ========== LOADING STATE ==========
    if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

    // Role display
    const roleName = user?.role?.name?.toLowerCase() || '';
    const roleDisplayName =
        user?.role?.display_name ||
        (roleName === 'kabag' ? 'Kepala Bagian' :
            roleName === 'ka_tu' ? 'Kepala Tata Usaha' :
                roleName === 'kalem' ? 'Kepala Lembaga' : 'Kaprodi');

    // ========== RENDER ==========
    return (
        <div className="space-y-5 animate-in">
            <Breadcrumb items={[{ label: 'Dashboard' }, { label: roleDisplayName }]} />

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-black text-teal-dark tracking-tight">
                        Dashboard {roleDisplayName}
                    </h1>
                    <p className="text-xs text-neutral-text-secondary mt-0.5 flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {prodi}
                    </p>
                </div>
                <Link to="/laporan/buat">
                    <button className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-main text-white text-sm font-semibold rounded-lg hover:bg-teal-light shadow-md transition-all">
                        <Plus className="w-4 h-4" /> Buat Laporan
                    </button>
                </Link>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KpiCard
                    icon={<FileText className="w-5 h-5 text-blue-600" />}
                    iconBg="bg-blue-100"
                    label="Laporan Saya"
                    value={filteredLaporan.length}
                    valueColor="text-blue-600"
                />
                <KpiCard
                    icon={<TrendingUp className="w-5 h-5 text-teal-600" />}
                    iconBg="bg-teal-100"
                    label="Tindak Lanjut"
                    value={tindakLanjutAktif.length}
                    valueColor="text-teal-600"
                    subtitle={`${avgProgress}% avg progress`}
                />
                <KpiCard
                    icon={<CheckCircle className="w-5 h-5 text-success" />}
                    iconBg="bg-success/10"
                    label="Selesai (Laporan)"
                    value={kpiData.selesai}
                    valueColor="text-success"
                />
                <KpiCard
                    icon={<AlertCircle className="w-5 h-5 text-danger" />}
                    iconBg="bg-danger/10"
                    label="Perlu Aksi"
                    value={overdueCount + kpiData.ditolak}
                    valueColor={overdueCount + kpiData.ditolak > 0 ? 'text-danger' : 'text-neutral-text-secondary'}
                    subtitle={overdueCount > 0 ? `${overdueCount} terlambat` : undefined}
                />
            </div>

            {/* Status Flow - Compact */}
            <Card padding="sm">
                <div className="flex items-center justify-between gap-1 overflow-x-auto">
                    {statusFlow.map((s, i) => (
                        <React.Fragment key={i}>
                            <div className="flex flex-col items-center gap-0.5 min-w-[50px]">
                                <div
                                    className="relative w-8 h-8 rounded-full text-white flex items-center justify-center"
                                    style={{ backgroundColor: s.bg }}
                                >
                                    {s.icon}
                                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-white text-[9px] font-bold text-neutral-text-primary border border-neutral-border flex items-center justify-center">
                                        {s.count}
                                    </span>
                                </div>
                                <span className="text-[9px] font-semibold text-neutral-text-secondary">
                                    {s.label}
                                </span>
                            </div>
                            {i < statusFlow.length - 1 && (
                                <ChevronRight className="w-3 h-3 text-neutral-border shrink-0" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </Card>

            {/* Alerts */}
            {(overdueCount > 0 || kpiData.ditolak > 0 || tindakLanjutMenungguKonfirmasi.length > 0) && (
                <div className="space-y-2">
                    {overdueCount > 0 && (
                        <div className="p-2.5 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-danger shrink-0" />
                            <p className="text-xs font-medium text-danger flex-1">
                                {overdueCount} tindak lanjut terlambat!
                            </p>
                            <Link to="/laporan?scope=pic&status=overdue" className="text-[10px] font-semibold text-danger hover:underline">
                                Lihat →
                            </Link>
                        </div>
                    )}
                    {kpiData.ditolak > 0 && (
                        <div className="p-2.5 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                            <p className="text-xs font-medium text-warning flex-1">
                                {kpiData.ditolak} laporan perlu diperbaiki
                            </p>
                            <Link to="/laporan?scope=pelapor&status=ditolak_l1" className="text-[10px] font-semibold text-warning hover:underline">
                                Lihat →
                            </Link>
                        </div>
                    )}
                    {tindakLanjutMenungguKonfirmasi.length > 0 && (
                        <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-2">
                            <Hourglass className="w-4 h-4 text-purple-600 shrink-0" />
                            <p className="text-xs font-medium text-purple-700 flex-1">
                                {tindakLanjutMenungguKonfirmasi.length} menunggu konfirmasi pimpinan
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left: Laporan & Tindak Lanjut */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Laporan Saya */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                Laporan Saya
                            </h3>
                            <Link
                                to="/laporan?scope=pelapor"
                                className="text-[10px] font-semibold text-teal-main hover:underline flex items-center gap-1"
                            >
                                Semua ({filteredLaporan.length}) <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                        <div className="space-y-2">
                            {filteredLaporan.length > 0 ? (
                                filteredLaporan.slice(0, 4).map((lap) => {
                                    const action = getStatusAction(lap.status);
                                    return (
                                        <Link
                                            key={lap.id}
                                            to={lap.status === 'draf' ? `/laporan/${lap.id}/edit` : `/laporan/${lap.id}`}
                                        >
                                            <div className="flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-neutral-bg/50 transition-colors">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <span className="text-[10px] font-mono text-teal-main">
                                                            {lap.kode}
                                                        </span>
                                                        <Badge variant="status" status={lap.status as StatusLaporan} />
                                                    </div>
                                                    <p className="text-xs font-medium truncate">{lap.judul}</p>
                                                </div>
                                                <button className="p-1.5 rounded-lg bg-teal-main/10 hover:bg-teal-main/20">
                                                    {action.icon}
                                                </button>
                                            </div>
                                        </Link>
                                    );
                                })
                            ) : (
                                <div className="text-center py-6">
                                    <FileText className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
                                    <p className="text-xs text-neutral-text-secondary">Belum ada laporan</p>
                                    <Link to="/laporan/buat" className="text-[10px] text-teal-main font-semibold hover:underline">
                                        Buat laporan pertama →
                                    </Link>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Tindak Lanjut Saya - COMPACT */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Tindak Lanjut Saya
                            </h3>
                            <Link
                                to="/laporan?scope=pic"
                                className="text-[10px] font-semibold text-teal-main hover:underline flex items-center gap-1"
                            >
                                Lihat Semua <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>

                        <div className="space-y-4">
                            {/* Aktif */}
                            <div>
                                <SectionHeader
                                    icon={<Clock className="w-3 h-3" />}
                                    iconColor="bg-warning"
                                    title="Aktif"
                                    count={Math.min(tindakLanjutAktif.length, MAX_ITEMS_DASHBOARD)}
                                    total={tindakLanjutAktif.length}
                                    linkTo="/laporan?scope=pic&status=berlangsung"
                                />
                                <div className="space-y-2">
                                    {tindakLanjutAktif.length > 0 ? (
                                        tindakLanjutAktif.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`aktif-${item.id}`}
                                                item={item}
                                                type="aktif"
                                                isBaruDialihkan={isBaruDialihkan(item)}
                                                compact
                                            />
                                        ))
                                    ) : (
                                        <EmptyState message="Tidak ada tindak lanjut aktif" />
                                    )}
                                </div>
                            </div>

                            {/* Menunggu Konfirmasi */}
                            {tindakLanjutMenungguKonfirmasi.length > 0 && (
                                <div>
                                    <SectionHeader
                                        icon={<Hourglass className="w-3 h-3" />}
                                        iconColor="bg-purple-500"
                                        title="Menunggu Konfirmasi"
                                        count={Math.min(tindakLanjutMenungguKonfirmasi.length, MAX_ITEMS_DASHBOARD)}
                                        total={tindakLanjutMenungguKonfirmasi.length}
                                        linkTo="/laporan?scope=pic&status=menunggu_konfirmasi"
                                    />
                                    <div className="space-y-2">
                                        {tindakLanjutMenungguKonfirmasi.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`menunggu-${item.id}`}
                                                item={item}
                                                type="menunggu_konfirmasi"
                                                compact
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Selesai */}
                            {tindakLanjutSelesai.length > 0 && (
                                <div>
                                    <SectionHeader
                                        icon={<CheckCircle className="w-3 h-3" />}
                                        iconColor="bg-success"
                                        title="Selesai"
                                        count={Math.min(tindakLanjutSelesai.length, MAX_ITEMS_DASHBOARD)}
                                        total={tindakLanjutSelesai.length}
                                        linkTo="/laporan?scope=pic&status=selesai"
                                    />
                                    <div className="space-y-2">
                                        {tindakLanjutSelesai.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`selesai-${item.id}`}
                                                item={item}
                                                type="selesai"
                                                compact
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Right: Sidebar */}
                <div className="space-y-5">
                    {/* Deadline Terdekat */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Deadline
                            </h3>
                        </div>
                        <div className="space-y-2">
                            {urgentDeadlines.length > 0 ? (
                                urgentDeadlines.map((item) => (
                                    <DeadlineCard
                                        key={`dl-${item.id}`}
                                        item={item}
                                        daysRemaining={getDaysRemaining(item)}
                                    />
                                ))
                            ) : (
                                <div className="text-center py-4">
                                    <CheckCircle className="w-6 h-6 mx-auto mb-1 text-success" />
                                    <p className="text-[10px] text-neutral-text-secondary">
                                        Tidak ada deadline mendesak
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Progress Summary */}
                    <Card>
                        <h3 className="text-sm font-bold text-teal-dark mb-1 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Ringkasan
                        </h3>
                        <p className="text-[10px] text-neutral-text-secondary leading-tight mb-3">
                            Ringkasan performa Anda sebagai Penanggung Jawab (PIC) Tindak Lanjut perbaikan.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] text-neutral-text-secondary">
                                        Progress Rata-rata
                                    </span>
                                    <span className="text-xs font-bold text-teal-main">
                                        {avgProgress}%
                                    </span>
                                </div>
                                <ProgressBar value={avgProgress} size="sm" color="teal" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-teal-main">
                                        {tindakLanjutAktif.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Aktif">TL Aktif</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-purple-600">
                                        {tindakLanjutMenungguKonfirmasi.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Menunggu Konfirmasi">TL Konfirmasi</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-success">
                                        {tindakLanjutSelesai.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Selesai">TL Selesai</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className={`text-lg font-black ${overdueCount > 0 ? 'text-danger' : 'text-neutral-text-secondary'}`}>
                                        {overdueCount}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Terlambat">TL Terlambat</p>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Quick Actions */}
                    <Card>
                        <h3 className="text-sm font-bold text-teal-dark mb-3 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Aksi Cepat
                        </h3>
                        <div className="space-y-2">
                            <Link to="/laporan/buat" className="block">
                                <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-teal-main/5 hover:border-teal-main/20 transition-colors text-left">
                                    <div className="p-1.5 rounded-lg bg-teal-main/10">
                                        <Plus className="w-3.5 h-3.5 text-teal-main" />
                                    </div>
                                    <span className="text-xs font-semibold">Buat Laporan Baru</span>
                                </button>
                            </Link>
                            <Link to="/laporan" className="block">
                                <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-blue-50 hover:border-blue-200 transition-colors text-left">
                                    <div className="p-1.5 rounded-lg bg-blue-100">
                                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                                    </div>
                                    <span className="text-xs font-semibold">Semua Laporan</span>
                                </button>
                            </Link>
                            {tindakLanjutAktif.length > 0 && (
                                <Link to={`/tindak-lanjut/${tindakLanjutAktif[0].id}`} className="block">
                                    <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-warning/5 hover:border-warning/20 transition-colors text-left">
                                        <div className="p-1.5 rounded-lg bg-warning/10">
                                            <TrendingUp className="w-3.5 h-3.5 text-warning" />
                                        </div>
                                        <span className="text-xs font-semibold">Update Progress</span>
                                    </button>
                                </Link>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DashboardKaprodi;
